// Hücre not penceresi (Dalga 2 K10 → Dalga 3 iş parçacığı): hücreye bağlı not thread'i + yazma alanı.
// Değer alanı yok — değer yazılamayan hücrede (denetçi, salt-okunur gösterge) ve not simgesi
// tıklanınca açılır. Müşteri: "Danışmanınıza not yazın" / "Gönder"; danışman: "Yanıtla". Thread boş
// hücrede de yazılır (değer satırı gerekmez, spec §6). Kopya: kpi-notes-copy.ts.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { NotesAudience } from "../../api/kpi-notes";
import { type CellPos, type GridColumn, type GridRow } from "../../lib/kpi-grid-nav";
import { KPI_NOTES_COPY } from "../../lib/kpi-notes-copy";
import { KpiCellNotes } from "./KpiCellNotes";
import { placementStyle, usePopoverPlacement } from "./use-popover-placement";

/**
 * Not yazılabilir hücre: yaprak işletme hücresi (aggregate/bant/yapı satırı değil). Türetilmiş (formüllü)
 * hücre de: değer düzenlenemez ama görünür yaprak hücreye not yazılır (spec §6, backend aynı kural).
 */
export function canNoteAt(rows: GridRow[], columns: GridColumn[], pos: CellPos): boolean {
  const row = rows[pos.row];
  const col = columns[pos.col];
  if (!row || row.isBand || !col?.entityId || col.aggregate) return false;
  return row.indicator.effectiveClientAccess !== "closed";
}

export function KpiCellNoteEditor(props: {
  audience: NotesAudience;
  setId: string;
  target: { rows: GridRow[]; columns: GridColumn[]; pos: CellPos };
  getAnchor: () => HTMLElement | null;
  onChanged: () => void;
  onClose: () => void;
}): JSX.Element | null {
  const { audience, setId, target, getAnchor, onChanged, onClose } = props;
  const indicator = target.rows[target.pos.row]?.indicator;
  const col = target.columns[target.pos.col];
  const rootRef = useRef<HTMLDivElement>(null);
  const pos = usePopoverPlacement(rootRef, getAnchor, []);
  const C = KPI_NOTES_COPY[audience];

  // Açılışta odak yazma alanına (yüklenince KpiCellNotes odaklar); Tab odak tuzağı diyalog içinde.
  useEffect(() => {
    rootRef.current?.querySelector<HTMLElement>("button, textarea")?.focus();
  }, []);
  function trapTab(e: React.KeyboardEvent): void {
    if (e.key !== "Tab" || !rootRef.current) return;
    const f = rootRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    if (f.length === 0) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!indicator || !col?.entityId) return null;
  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label={`${indicator.label} · ${col.label}`}
      data-testid="kpi-note-editor"
      className="rounded-xl p-4"
      data-placement={pos.placement}
      style={{
        ...placementStyle(pos),
        width: "min(380px, calc(100vw - 16px))",
        maxHeight: "min(640px, calc(100vh - 24px))",
        overflow: "auto",
        fontFamily: "var(--font-sans)",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-md, 0 12px 32px rgba(15,42,82,.18))",
      }}
      onKeyDown={(e) => {
        trapTab(e);
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="font-semibold text-sm mb-2" style={{ color: "var(--color-text)" }}>
        {indicator.label} · {col.label}
      </div>
      <KpiCellNotes
        audience={audience}
        setId={setId}
        cell={{ metricId: indicator.metricId, entityId: col.entityId, year: col.year }}
        composer
        autoFocus
        onChanged={onChanged}
      />
      <div className="flex justify-end mt-3">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg text-sm"
          style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
        >
          {C.close}
        </button>
      </div>
    </div>,
    document.body,
  );
}
